import React, { useEffect, useMemo, useRef, useState } from "react";

import styled from "styled-components";

import ManuscriptAddModal from "./modal/ManuscriptAddModal";

import HtmlHead from "components/atoms/HtmlHead";
import Icon from "components/atoms/Icon";
import { CountTagItem, TagText } from "components/atoms/Tag";
import Tooltip from "components/atoms/Tooltip";
import Alert from "components/atoms/alert/Alert";
import Confirm from "components/atoms/alert/Confirm";
import Button from "components/atoms/button";
import Checkbox from "components/atoms/input/Checkbox";
import SelectBox from "components/atoms/input/SelectBox";
import Text from "components/atoms/text/Text";
import useToast from "components/atoms/toast/useToast";

import { BodyWithBreadcrumb, Breadcrumb, BreadcrumbTitle } from "components/molecules/Breadcrumb";
import Table from "components/molecules/table/Table";
import {
    compareColumns,
    getColumnOrder,
    getColumnSize,
    getColumnVisibility,
    updateColumnOrder,
} from "components/molecules/table/TableManager";
import LoginUserLayout from "components/templates/LoginUserLayout";

import { useManuscriptBulkDelete, useManuscriptUpdate, useManuscripts } from "hooks/queries/usePapers";
import { useUser } from "hooks/queries/useUser";

import { isReadonlyUser } from "utils/user";
import { genericEnterKeyDown } from "utils/validation";

import moment from "moment/moment";

const HeaderLeftWrap = styled.div`
    display: flex;
    align-items: end;
    gap: 8px;
`;

const HeaderRightWrap = styled.div`
    position: relative;
    margin: 0 0 0 40px;
`;

const BodyHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: flex-end;
    padding: 16px 0;
`;

const BodyHeaderLeftSection = styled.div`
    display: flex;
    flex: 1;
    align-items: center;
    width: 171px;
    margin-left: 10px;
`;

const BodyHeaderRightSection = styled.div`
    display: flex;
    flex: 1;
    align-items: center;
    justify-content: flex-end;
    margin: 0 0 0 24px;
`;

const SearchWrap = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
    min-width: 200px;
    max-width: 320px;
`;

const SearchInputBox = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 1px 4px;
    width: 100%;
    height: 28px;
    background-color: var(--color-White);
    border: solid 1px var(--color-Line);
    border-radius: 4px;

    &:focus-within {
        border: solid 1px var(--color-Key);
    }
`;

const SearchInput = styled.input`
    padding: 0 8px;
    width: calc(100% - 24px);
    height: 24px;
    font-size: 13px;
    font-weight: 400;
    color: var(--color-Black);
    background-color: transparent;
    border: transparent;
    outline: var(--color-Key);
`;

const SearchButtonWrap = styled.div`
    display: flex;
    align-items: center;
    height: 24px;
`;

const BlankView = styled.div`
    flex: 1;
    display: flex;
    justify-content: center;
    align-items: center;
`;

const BlankText = styled(Text).attrs({
    variant: "body-lg-400",
    color: "var(--color-SubBlack)",
})``;

const defaultSearchParams = {
    page_size: 10,
    page: 1,
    search: "",
    ordering: "",
};

//region: 컴포넌트 정의 시작
function ManuscriptListPage() {
    const openNewTab = (path) => window.open(path, "_blank", "noopener noreferrer");

    const { setToast } = useToast();

    const [user, setUser] = useState({});
    const userQuery = useUser();
    useEffect(() => {
        if (userQuery.data) {
            setUser(userQuery.data);
        }
    }, [userQuery.data]);

    const [searchFilter, setSearchFilter] = useState("");
    const [searchParams, setSearchParams] = useState({ ...defaultSearchParams });
    const manuscriptQuery = useManuscripts(searchParams);
    const [manuscripts, setManuscripts] = useState({
        count: 0,
        results: [],
    });
    const tableRef = useRef();

    useEffect(() => {
        return () => {
            manuscriptQuery.remove();
        };
    }, []);

    useEffect(() => {
        if (manuscriptQuery.data) {
            setManuscripts(manuscriptQuery.data);
        }
    }, [manuscriptQuery.data]);

    const updateManuscriptMutation = useManuscriptUpdate();
    const deleteBulkManuscriptMutation = useManuscriptBulkDelete();

    const [showManuscriptAddModal, setShowManuscriptAddModal] = useState(false);

    // region: 체크박스 선택 동작
    const checkedManuscriptsRef = useRef([]);

    const handleCheckboxAllChange = (e) => {
        // 전체 선택이면 모든 id를, 그렇지 않으면 빈 배열을 반환
        checkedManuscriptsRef.current = e.target.checked
            ? manuscripts?.results?.map((manuscript) => manuscript.id)
            : [];
        rerenderTable();
    };

    const handleCheckboxChange = (event) => {
        let { id } = event.target;
        id = Number(id);
        handleCheckboxChangeById(id);
    };

    const handleCheckboxChangeById = (id) => {
        const index = checkedManuscriptsRef.current.indexOf(id);
        if (index > -1) {
            checkedManuscriptsRef.current = checkedManuscriptsRef.current.filter((item) => item !== id);
        } else {
            checkedManuscriptsRef.current = [...checkedManuscriptsRef.current, id];
        }
        rerenderTable();
    };

    const handleExecuteCheckedItemsAction = (checkedItemsAction) => {
        if (isReadonlyUser(user)) {
            setToast("읽기 전용 사용자 계정은 해당 기능을 사용할 수 없습니다.", "warn");
            return;
        }
        if (checkedItemsAction === "delete") {
            Confirm("warn", "삭제", "선택 항목을 삭제하시겠습니까?", "삭제", (e) => {
                deleteBulkManuscriptMutation.mutate(
                    { ids: checkedManuscriptsRef.current },
                    {
                        onSuccess: () => {
                            setToast("성공적으로 삭제되었습니다.", "info");
                            if (
                                manuscripts.results.length === checkedManuscriptsRef.current.length &&
                                searchParams.page > 1
                            ) {
                                setSearchParams({
                                    ...searchParams,
                                    page: searchParams.page - 1,
                                });
                            } else {
                                manuscriptQuery.refetch();
                            }
                            checkedManuscriptsRef.current = [];
                        },
                        onError: (error) => {
                            Alert("warn", "에러", "삭제 중 오류가 발생했습니다.");
                        },
                    },
                );
            });
        }
    };

    //region: 테이블 컬럼 정의
    const PAGE_KEY = "manuscriptList";
    const rerenderTable = React.useReducer(() => ({}), {})[1];

    const [tableConfig, setTableConfig] = useState(
        localStorage.getItem(PAGE_KEY) ? JSON.parse(localStorage.getItem(PAGE_KEY)).manuscript : [],
    );

    useEffect(() => {
        const config = compareColumns(columns, tableConfig);
        setTableConfig(config);
        localStorage.setItem(
            PAGE_KEY,
            JSON.stringify({
                manuscript: tableConfig,
            }),
        );
    }, [tableConfig]);

    const columns = useMemo(
        () => [
            {
                id: "id",
                accessorKey: "id",
                header: null,
                name: "선택",
                size: 36,
                minSize: 36,
                maxSize: 36,
                enableResizing: false,
                defaultVisibility: true,
                content: (info) => {
                    const value = info.getValue();
                    return (
                        <Checkbox
                            id={value}
                            checked={checkedManuscriptsRef.current.includes(value)}
                            onChange={handleCheckboxChange}
                        />
                    );
                },
            },
            {
                id: "is_favorite",
                accessorKey: "is_favorite",
                header: null,
                name: "즐겨찾기",
                size: 36,
                minSize: 36,
                maxSize: 36,
                enableResizing: false,
                defaultVisibility: true,
                content: (info) => {
                    return (
                        <Button
                            onlyIcon
                            onClick={(e) => {
                                e.stopPropagation();
                                updateManuscriptMutation.mutate(
                                    {
                                        id: info.row.original.id,
                                        is_favorite: !info.getValue(),
                                    },
                                    {
                                        onSuccess: () => {
                                            rerenderTable();
                                        },
                                    },
                                );
                            }}
                            bgColor={"transparent"}
                            hoverBgColor={"transparent"}
                            disabled={isReadonlyUser(user)}
                        >
                            {info.row.original.is_favorite ? (
                                <Icon name={"bookmark"} color={"var(--color-Key)"} size={"16px"} />
                            ) : (
                                <Icon name={"bookmarkDisactive"} size={"16px"} />
                            )}
                        </Button>
                    );
                },
            },
            {
                id: "display_name",
                accessorKey: "display_name",
                name: "제목",
                size: getColumnSize("display_name", tableConfig) || 500,
                text: (info) => info.getValue(),
                onTextClick: (info) => {
                    openNewTab(
                        `${process.env.REACT_APP_OVERLEAF_BASE_URL}/prep/auth?return_to=/project/${info.row.original.overleaf_project_id}`,
                    );
                },
            },
            {
                id: "overleaf_file_size",
                accessorKey: "overleaf_file_size",
                name: "파일 크기",
                size: getColumnSize("overleaf_file_size", tableConfig) || 120,
                text: (info) => {
                    const value = info.getValue();
                    return `${(value / 1024 / 1024).toFixed(2)} MB`;
                },
            },
            {
                id: "created_at",
                accessorKey: "created_at",
                name: "생성일시",
                size: getColumnSize("created_at", tableConfig) || 160,
                text: (info) => moment(info.getValue()).format("YYYY-MM-DD HH:mm:ss"),
            },
            {
                id: "updated_at",
                accessorKey: "updated_at",
                name: "수정일시",
                size: getColumnSize("updated_at", tableConfig) || 160,
                text: (info) => moment(info.getValue()).format("YYYY-MM-DD HH:mm:ss"),
            },
        ],
        [],
    );

    const handlePageChange = (newPage) => {
        setSearchParams({ ...searchParams, page: newPage });
    };

    const handlePageSizeChange = (newSize) => {
        setSearchParams({ ...searchParams, page_size: newSize, page: 1 });
    };

    const handleSortChange = (sort) => {
        const sortValue = sort
            .map((sortItem) => {
                return sortItem.desc ? `-${sortItem.id}` : sortItem.id;
            })
            .join(",");
        setSearchParams({ ...searchParams, ordering: sortValue, page: 1 });
    };

    useEffect(() => {
        checkedManuscriptsRef.current = [];
        rerenderTable();
    }, [manuscripts?.results]);

    //region: 마크업 시작
    return (
        <LoginUserLayout>
            <HtmlHead title={"논문작성"} />
            <Breadcrumb>
                <HeaderLeftWrap>
                    <BreadcrumbTitle>논문작성</BreadcrumbTitle>
                    <CountTagItem>
                        <TagText>{manuscripts.count}건</TagText>
                    </CountTagItem>
                </HeaderLeftWrap>
                <HeaderRightWrap>
                    <Button
                        onClick={() => setShowManuscriptAddModal(true)}
                        width={"132px"}
                        height={"32px"}
                        bgColor={"var(--color-Button2)"}
                        hoverBgColor={"var(--color-ButtonHover2)"}
                        border={"solid 2px transparent"}
                        fontColor={"var(--color-White)"}
                        buttonText={"논문원고 추가"}
                        disabled={isReadonlyUser(user)}
                    >
                        <Icon name={"plus"} size={"12"} color={"var(--color-White)"} />
                    </Button>
                </HeaderRightWrap>
            </Breadcrumb>

            <BodyWithBreadcrumb>
                <BodyHeader>
                    <BodyHeaderLeftSection>
                        <Checkbox
                            margin={"0 20px 0 0"}
                            indeterminate={
                                checkedManuscriptsRef.current.length > 0 &&
                                checkedManuscriptsRef.current.length < manuscripts?.results?.length
                            }
                            checked={
                                manuscripts?.results?.length > 0 &&
                                checkedManuscriptsRef.current.length === manuscripts?.results?.length
                            }
                            onChange={handleCheckboxAllChange}
                        />
                        <SelectBox
                            width={"auto"}
                            height={"28px"}
                            optionWidth={"200px"}
                            optionAlign={"left"}
                            optionMaxHeight={"auto"}
                            items={[
                                { id: "", name: "--- 액션을 선택하세요. ---" },
                                { id: "delete", name: "선택된 항목을 삭제합니다." },
                            ]}
                            textAlign={"left"}
                            value={""}
                            onChange={(value) => {
                                if (checkedManuscriptsRef.current.length === 0) {
                                    setToast("선택된 항목이 없습니다.", "warn");
                                    return;
                                }
                                handleExecuteCheckedItemsAction(value);
                            }}
                        />
                        {checkedManuscriptsRef.current.length > 0 && (
                            <Text margin="0 0 0 10px" variant="body-sm-400" fontWeight="300">
                                {checkedManuscriptsRef.current.length}개의 항목이 선택됨
                            </Text>
                        )}
                    </BodyHeaderLeftSection>
                    <BodyHeaderRightSection>
                        <SearchWrap>
                            <SearchInputBox>
                                <SearchInput
                                    value={searchFilter}
                                    onChange={(e) => setSearchFilter(e.target.value)}
                                    onKeyDown={(e) => {
                                        genericEnterKeyDown(e, () => {
                                            setSearchParams({ ...searchParams, search: searchFilter, page: 1 });
                                        });
                                    }}
                                />
                                <SearchButtonWrap>
                                    {searchFilter && (
                                        <Tooltip message={"초기화"}>
                                            <Button
                                                onlyIcon
                                                bgColor={"var(--color-White)"}
                                                hoverBgColor={"var(--color-ButtonHover4)"}
                                                onClick={() => {
                                                    setSearchFilter("");
                                                    setSearchParams({ ...searchParams, search: "", page: 1 });
                                                }}
                                            >
                                                <Icon name={"x"} color={"var(--color-SubBlack)"} />
                                            </Button>
                                        </Tooltip>
                                    )}
                                    <Tooltip message={"검색"}>
                                        <Button
                                            onlyIcon
                                            bgColor={"var(--color-White)"}
                                            hoverBgColor={"var(--color-ButtonHover4)"}
                                            onClick={() => {
                                                setSearchParams({
                                                    ...searchParams,
                                                    search: searchFilter,
                                                    page: 1,
                                                });
                                            }}
                                        >
                                            <Icon name={"search"} size={"12"} color={"var(--color-SubBlack)"} />
                                        </Button>
                                    </Tooltip>
                                </SearchButtonWrap>
                            </SearchInputBox>
                        </SearchWrap>
                    </BodyHeaderRightSection>
                </BodyHeader>

                {manuscripts?.count !== 0 ? (
                    <Table
                        ref={tableRef}
                        data={manuscripts?.results || []}
                        columns={columns}
                        useSizingColumns={true}
                        useSortingColumns={true}
                        useOrderColumns={true}
                        pagination={{
                            pageIndex: searchParams.page,
                            pageSize: searchParams.page_size,
                            count: manuscripts?.count,
                        }}
                        setPagination={(page) => {
                            if (Number(searchParams.page_size) !== Number(page.pageSize)) {
                                handlePageSizeChange(page.pageSize);
                            } else if (searchParams.page !== Number(page.pageIndex)) {
                                handlePageChange(page.pageIndex);
                            }
                        }}
                        sorting={
                            searchParams?.ordering
                                ? [
                                      {
                                          id: searchParams.ordering.replace("-", ""),
                                          desc: searchParams.ordering.startsWith("-"),
                                      },
                                  ]
                                : []
                        }
                        setSorting={handleSortChange}
                        onRowClick={(row) => {
                            handleCheckboxChangeById(row.original.id);
                        }}
                        onSizeChange={(changedColumn) => {
                            Object.keys(changedColumn).forEach((key) => {
                                setTableConfig((prev) =>
                                    prev.map((item) =>
                                        item.id === key ? { ...item, size: changedColumn[key] } : item,
                                    ),
                                );
                            });
                        }}
                        columnVisibility={getColumnVisibility(tableConfig)}
                        columnOrder={getColumnOrder(tableConfig)}
                        setColumnOrder={(oldIndex, newIndex) =>
                            setTableConfig(updateColumnOrder(tableConfig, oldIndex, newIndex))
                        }
                        checkedRowIds={checkedManuscriptsRef.current}
                    />
                ) : (
                    <BlankView>
                        <BlankText>
                            {searchParams.search ? "검색 결과가 없습니다." : "작성중인 논문원고가 없습니다."}
                        </BlankText>
                    </BlankView>
                )}
            </BodyWithBreadcrumb>
            {showManuscriptAddModal && (
                <ManuscriptAddModal
                    exitModal={(e) => setShowManuscriptAddModal(false)}
                    onSuccess={(data) => {
                        if (data.overleaf_project_id) {
                            openNewTab(
                                `${process.env.REACT_APP_OVERLEAF_BASE_URL}/prep/auth?return_to=/project/${data.overleaf_project_id}`,
                            );
                        }
                    }}
                />
            )}
        </LoginUserLayout>
    );
}

export default ManuscriptListPage;
